import React, { useRef, useState, useCallback } from 'react';

const ErrorMsg = ({ msg }) => (
  msg ? (
    <div className="flex items-center gap-1 mt-1.5 animate-fadeIn">
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
        <circle cx="8" cy="8" r="7" stroke="#EF4444" strokeWidth="1.5"/>
        <path d="M8 5v3.5M8 11h.01" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
      <p className="text-sm" style={{ color: '#EF4444' }}>{msg}</p>
    </div>
  ) : null
);

export default function Step1FounderInfo({ formData, errors, onChange, onContinue, onSetErrors }) {
  const fileInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoSuccess, setPhotoSuccess] = useState(false);
  const [shakeBtn, setShakeBtn] = useState(false);
  const [touched, setTouched] = useState({});

  const ACCEPTED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_PHOTO_SIZE = 5 * 1024 * 1024;

  const validateField = useCallback((name, value) => {
    switch (name) {
      case 'fullName':
        if (!value?.trim()) return 'Please enter your full name';
        if (value?.trim()?.length < 2) return 'Please enter your full name';
        return '';
      case 'email':
        if (!value?.trim()) return 'Please enter a valid email address';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(value)) return 'Please enter a valid email address';
        return '';
      case 'phone':
        if (!value?.trim()) return 'Please enter a valid phone number';
        if (value?.replace(/\D/g, '')?.length < 10) return 'Please enter a valid phone number';
        return '';
      case 'linkedinUrl':
        if (value?.trim() && !value?.trim()?.includes('linkedin.com/in/')) return 'Please enter a valid LinkedIn profile URL';
        return '';
      case 'city':
        if (!value?.trim()) return 'Please enter your city';
        return '';
      case 'state':
        if (!value?.trim()) return 'Please select your state';
        return '';
      default:
        return '';
    }
  }, []);

  const handleBlur = (name, value) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    onSetErrors(prev => ({ ...prev, [name]: err || undefined }));
  };

  const handleChange = (name, value) => {
    onChange({ [name]: value });
    if (touched?.[name]) {
      const err = validateField(name, value);
      onSetErrors(prev => ({ ...prev, [name]: err || undefined }));
    }
  };

  const handlePhotoFile = (file) => {
    if (!file) return;
    setPhotoError('');
    setPhotoSuccess(false);
    if (!ACCEPTED_PHOTO_TYPES?.includes(file?.type)) {
      setPhotoError('Please upload a JPG, PNG, or WEBP image');
      onChange({ profilePhoto: null, profilePhotoPreview: null });
      if (fileInputRef?.current) fileInputRef.current.value = '';
      return;
    }
    if (file?.size > MAX_PHOTO_SIZE) {
      setPhotoError('File size must be under 5MB');
      onChange({ profilePhoto: null, profilePhotoPreview: null });
      if (fileInputRef?.current) fileInputRef.current.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      onChange({ profilePhoto: file, profilePhotoPreview: e?.target?.result });
      setPhotoSuccess(true);
    };
    reader?.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    if (file) handlePhotoFile(file);
  };

  const handleContinueClick = () => {
    const fields = ['fullName', 'email', 'phone', 'linkedinUrl', 'city', 'state'];
    const newErrors = {};
    fields?.forEach(f => {
      const err = validateField(f, formData?.[f] || '');
      if (err) newErrors[f] = err;
    });
    setTouched(fields?.reduce((a, f) => ({ ...a, [f]: true }), {}));
    if (Object.keys(newErrors)?.length > 0) {
      onSetErrors(prev => ({ ...prev, ...newErrors }));
      setShakeBtn(true);
      setTimeout(() => setShakeBtn(false), 600);
      const firstErrField = document.querySelector('[data-field-error="true"]');
      if (firstErrField) firstErrField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    onContinue();
  };

  const inputClass = (field) => {
    const hasError = errors?.[field];
    const isValid = touched?.[field] && !hasError && formData?.[field]?.trim();
    return `w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all duration-200 ${
      hasError ? 'border-red-400 bg-red-50' : isValid ? 'border-[#D4A520]' : 'border-gray-200 focus:border-[#D4A520]'
    }`;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  return (
    <div>
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeInUp 0.2s ease-out forwards; }
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
        .shake-anim { animation: shake 0.5s ease-in-out; }
      `}</style>
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-1" style={{ color: '#0B1F3B', fontFamily: 'Cormorant Garamond, serif' }}>Tell Us About Yourself</h2>
        <p className="text-sm" style={{ color: '#6B7280' }}>Step 1 of 4 — Founder Information</p>
      </div>
      {/* Profile Photo Upload */}
      <div className="mb-6">
        <label className="block text-sm font-semibold mb-2" style={{ color: '#0B1F3B' }}>Profile Photo <span className="text-gray-400 font-normal">(optional)</span></label>
        <div
          className={`relative border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer transition-colors ${
            photoError ? 'border-red-400 bg-red-50' : dragging ? 'border-[#D4A520] bg-[#D4A520]/5' : photoSuccess ? 'border-[#D4A520] bg-[#D4A520]/5' : 'border-gray-200 hover:border-[#D4A520] hover:bg-[#D4A520]/5'
          }`}
          onClick={() => fileInputRef?.current?.click()}
          onDragOver={(e) => { e?.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          {formData?.profilePhotoPreview ? (
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                <img src={formData?.profilePhotoPreview} alt="Profile preview" className="w-20 h-20 rounded-full object-cover border-4" style={{ borderColor: '#D4A520' }} />
                <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#22C55E' }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l2.5 2.5L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold" style={{ color: '#0B1F3B' }}>{formData?.profilePhoto?.name}</p>
                <p className="text-xs" style={{ color: '#6B7280' }}>{formatFileSize(formData?.profilePhoto?.size)} · <span style={{ color: '#D4A520' }}>Click to change</span></p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              {photoError ? (
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#FEF2F2' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2"/><path d="M15 9l-6 6M9 9l6 6" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/></svg>
                </div>
              ) : (
                <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: '#F3F4F6' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="8" r="4" stroke="#D4A520" strokeWidth="2"/>
                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="#D4A520" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              )}
              <p className="text-sm font-medium" style={{ color: '#0B1F3B' }}>Drag & drop or click to upload</p>
              <p className="text-xs" style={{ color: '#9CA3AF' }}>JPG, PNG, WEBP up to 5MB</p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => handlePhotoFile(e?.target?.files?.[0])}
          />
        </div>
        {photoError && (
          <div className="flex items-center gap-1 mt-1.5 animate-fadeIn">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
              <circle cx="8" cy="8" r="7" stroke="#EF4444" strokeWidth="1.5"/>
              <path d="M8 5v3.5M8 11h.01" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <p className="text-sm" style={{ color: '#EF4444' }}>{photoError}</p>
          </div>
        )}
      </div>
      {/* Form Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div data-field-error={!!errors?.fullName}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Full Name <span style={{ color: '#D4A520' }}>*</span></label>
          <input
            type="text"
            value={formData?.fullName}
            onChange={(e) => handleChange('fullName', e?.target?.value)}
            onBlur={(e) => handleBlur('fullName', e?.target?.value)}
            placeholder="Jane Smith"
            className={inputClass('fullName')}
            style={{ color: '#0B1F3B' }}
          />
          <ErrorMsg msg={errors?.fullName} />
        </div>
        <div data-field-error={!!errors?.email}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Email Address <span style={{ color: '#D4A520' }}>*</span></label>
          <input
            type="email"
            value={formData?.email}
            onChange={(e) => handleChange('email', e?.target?.value)}
            onBlur={(e) => handleBlur('email', e?.target?.value)}
            placeholder="jane@company.com"
            className={inputClass('email')}
            style={{ color: '#0B1F3B' }}
          />
          <ErrorMsg msg={errors?.email} />
        </div>
        <div data-field-error={!!errors?.phone}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Phone Number <span style={{ color: '#D4A520' }}>*</span></label>
          <input
            type="tel"
            value={formData?.phone}
            onChange={(e) => handleChange('phone', e?.target?.value)}
            onBlur={(e) => handleBlur('phone', e?.target?.value)}
            placeholder="(555) 000-0000"
            className={inputClass('phone')}
            style={{ color: '#0B1F3B' }}
          />
          <ErrorMsg msg={errors?.phone} />
        </div>
        <div data-field-error={!!errors?.linkedinUrl}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>LinkedIn Profile <span className="text-gray-400 font-normal">(optional)</span></label>
          <input
            type="url"
            value={formData?.linkedinUrl}
            onChange={(e) => handleChange('linkedinUrl', e?.target?.value)}
            onBlur={(e) => handleBlur('linkedinUrl', e?.target?.value)}
            placeholder="https://linkedin.com/in/yourname"
            className={`w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all duration-200 ${
              errors?.linkedinUrl ? 'border-red-400 bg-red-50' : touched?.linkedinUrl && formData?.linkedinUrl && !errors?.linkedinUrl ? 'border-[#D4A520]' : 'border-gray-200 focus:border-[#D4A520]'
            }`}
            style={{ color: '#0B1F3B' }}
          />
          <ErrorMsg msg={errors?.linkedinUrl} />
        </div>
        <div data-field-error={!!errors?.city}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>City <span style={{ color: '#D4A520' }}>*</span></label>
          <input
            type="text"
            value={formData?.city}
            onChange={(e) => handleChange('city', e?.target?.value)}
            onBlur={(e) => handleBlur('city', e?.target?.value)}
            placeholder="New York"
            className={inputClass('city')}
            style={{ color: '#0B1F3B' }}
          />
          <ErrorMsg msg={errors?.city} />
        </div>
        <div data-field-error={!!errors?.state}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>State <span style={{ color: '#D4A520' }}>*</span></label>
          <input
            type="text"
            value={formData?.state}
            onChange={(e) => handleChange('state', e?.target?.value)}
            onBlur={(e) => handleBlur('state', e?.target?.value)}
            placeholder="NY"
            className={inputClass('state')}
            style={{ color: '#0B1F3B' }}
          />
          <ErrorMsg msg={errors?.state} />
        </div>
      </div>
      {/* Required legend */}
      <p className="text-xs mb-4" style={{ color: '#9CA3AF' }}>* Required fields</p>
      <button
        onClick={handleContinueClick}
        className={`w-full py-4 rounded-xl font-bold text-base mt-2 transition-all duration-200 hover:opacity-90 active:scale-[0.98] ${shakeBtn ? 'shake-anim' : ''}`}
        style={{ background: '#D4A520', color: '#0B1F3B' }}
      >
        Continue →
      </button>
    </div>
  );
}
